import { useState } from "react";
import { Mail, MapPin, Building2, Send, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/shared";
import { api, apiError } from "@/lib/api";

const REGIONS = [
  { k: "emea", city: "London / Frankfurt / Vilnius", desc: "Regulatory alignment, real estate tokenization and institutional capital across the EMEA region.", email: "emea@auvrarwa.com" },
  { k: "apac", city: "Singapore / Hong Kong / Tokyo", desc: "Digital asset ecosystem, blockchain partnerships and cross-border tokenization across APAC.", email: "apac@auvrarwa.com" },
  { k: "americas", city: "New York / Miami / São Paulo", desc: "Institutional capital, technology partnerships and treasury infrastructure across the Americas.", email: "americas@auvrarwa.com" },
  { k: "global", city: "Global Desk · 24/7", desc: "Operational support, general enquiries and platform onboarding — routed to the closest team member.", email: "hello@auvrarwa.com" },
];

const INQUIRY = ["Institutional", "Asset Originator", "Partnership", "Compliance", "Press", "General"];

export default function Contact({ onCta }) {
  const [f, setF] = useState({ name: "", email: "", company: "", region: "global", inquiry_type: "General", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) { toast.error("Please complete the required fields"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/contact", f);
      toast.success(`Message received — routed to ${data.routed_to}`);
      setF({ ...f, name: "", email: "", company: "", message: "" });
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <>
      <Navbar onCta={onCta} />
      <main className="pt-36 pb-24">
        <div className="container-auvra">
          <div className="max-w-3xl">
            <Reveal><span className="kicker mb-4 block">Contact</span></Reveal>
            <Reveal i={1}>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tighter leading-[1.05]">
                Speak directly to the <span className="text-auvra-blue">AUVRA</span> team
              </h1>
            </Reveal>
            <Reveal i={2}>
              <p className="mt-6 text-lg text-secondary-auvra leading-relaxed">
                Institutional desks, asset originators, partners and press. Choose a region and our team responds within 24 hours — 4 hours for institutional enquiries.
              </p>
            </Reveal>
          </div>

          {/* Region cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
            {REGIONS.map((r, i) => (
              <Reveal key={r.k} i={i}>
                <div className="glass rounded-2xl p-6 h-full hover:border-auvra-blue/30 transition-colors">
                  <MapPin className="w-5 h-5 text-auvra-blue mb-4" />
                  <div className="text-xs uppercase tracking-[0.2em] text-secondary-auvra">{r.k}</div>
                  <div className="text-lg font-semibold mt-1 mb-3">{r.city}</div>
                  <p className="text-sm text-secondary-auvra leading-relaxed">{r.desc}</p>
                  <a href={`mailto:${r.email}`} className="mt-4 inline-flex text-sm text-auvra-blue hover:underline">{r.email}</a>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Form + info */}
          <div className="grid lg:grid-cols-5 gap-6 mt-16">
            <Reveal className="lg:col-span-3">
              <form onSubmit={submit} className="glass rounded-2xl p-8 space-y-5" data-testid="contact-form">
                <h2 className="text-2xl font-semibold tracking-tight">Send us a message</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField label="Name *"><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name" className={inputCls} /></FormField>
                  <FormField label="Email *"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email" className={inputCls} /></FormField>
                  <FormField label="Company"><input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="contact-company" className={inputCls} /></FormField>
                  <FormField label="Region"><select value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} data-testid="contact-region" className={inputCls}>
                    <option value="emea">EMEA</option><option value="apac">APAC</option><option value="americas">Americas</option><option value="global">Global</option>
                  </select></FormField>
                  <div className="md:col-span-2"><FormField label="Inquiry Type">
                    <div className="flex flex-wrap gap-2">
                      {INQUIRY.map((i) => (
                        <button type="button" key={i} onClick={() => setF({ ...f, inquiry_type: i })} data-testid={`inq-${i}`}
                          className={`text-xs px-4 py-2 rounded-full border transition-colors ${f.inquiry_type === i ? "bg-auvra-blue border-auvra-blue text-white" : "border-white/15 text-secondary-auvra hover:border-white/40"}`}>
                          {i}
                        </button>
                      ))}
                    </div>
                  </FormField></div>
                  <div className="md:col-span-2"><FormField label="Message *">
                    <textarea required rows={6} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message" className={inputCls} />
                  </FormField></div>
                </div>
                <button disabled={busy} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-auvra-blue hover:bg-auvra-blue/90 disabled:opacity-60 text-white font-medium px-6 py-3 rounded-full transition-all hover:shadow-[0_0_30px_rgba(37,99,235,0.5)]">
                  <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send Message"}
                </button>
              </form>
            </Reveal>
            <Reveal i={1} className="lg:col-span-2 space-y-4">
              <InfoCard Icon={Mail} title="Investor Relations" line="ir@auvrarwa.com" note="Institutional investors and family offices." />
              <InfoCard Icon={Building2} title="Corporate HQ" line="Vilnius, Lithuania" note="AUVRA RWA UAB · European operations base." />
              <InfoCard Icon={PhoneCall} title="Institutional Desk" line="Direct-relationship coverage" note="Response SLA: 4 hours (Mon–Fri, 24/7 global rotation)." />
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

const inputCls = "w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-auvra-blue transition-colors";

function FormField({ label, children }) {
  return <label className="block"><span className="block text-xs uppercase tracking-wider text-secondary-auvra mb-2">{label}</span>{children}</label>;
}

function InfoCard({ Icon, title, line, note }) {
  return (
    <div className="glass rounded-2xl p-6">
      <Icon className="w-5 h-5 text-auvra-blue mb-3" />
      <div className="text-xs uppercase tracking-wider text-secondary-auvra">{title}</div>
      <div className="text-lg font-medium mt-1">{line}</div>
      <div className="text-xs text-secondary-auvra mt-2">{note}</div>
    </div>
  );
}
